import { Box, Flex, Icon, Text } from "@chakra-ui/react";

/**
 * Empty-state CTA tile shown at the bottom of the lists feed (per Figma).
 * Vertical layout — circled plus icon stacked above the label — to read
 * as a "drop point" for a new list rather than a thin row of buttons.
 *
 * Figma spec for the tile container:
 *   Flow: Vertical
 *   Width: Fill (100%)
 *   Height: Hug (≈144px) — content-driven; we let py drive the height
 *           rather than hardcoding it so font-size or icon-size tweaks
 *           don't desync the box from its content.
 *   Radius: 16px
 *   Border: 1px dashed (5px on / 5px off)
 *   Padding: 40px top/bottom
 *   Gap: 8px
 *
 * The 5px-on / 5px-off dash pattern can't be controlled via CSS
 * `border-style: dashed` (browsers pick their own dash length), so we
 * render the border as an absolutely-positioned SVG rect with an
 * explicit `stroke-dasharray="5 5"`. Both the icon's currentColor and
 * the SVG stroke share the parent's `color` so hover-state shifts
 * cascade through to all three elements at once.
 */
export function NewListCard({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <Flex
      as="button"
      type="button"
      direction="column"
      align="center"
      justify="center"
      gap="8px"
      width="100%"
      py="40px"
      flexShrink={0}
      position="relative"
      borderRadius="16px"
      // No CSS border — the dashed border is the SVG below. We still
      // give the button its rounded clip via borderRadius so any
      // background fill on hover stays inside the corners.
      bg="transparent"
      color="neutral.300"
      transition="background 120ms ease-out, color 120ms ease-out"
      _hover={{
        bg: "transparent.white.05",
        color: "neutral.white",
      }}
      onClick={onClick}
    >
      {/*
        Dashed border drawn via SVG so we can spec the exact 5-on / 5-off
        pattern from Figma. The rect's stroke is currentColor so the
        outer Flex's color/_hover cascade applies. preserveAspectRatio
        is "none" because the rect's width/height are 100% — we want the
        rounded rectangle to stretch to the box, not center inside a
        squarified viewBox.
      */}
      <Box
        as="svg"
        position="absolute"
        inset={0}
        width="100%"
        height="100%"
        pointerEvents="none"
        // SVG attributes that Chakra doesn't strongly type — pass via
        // any extras since they're string-typed on the underlying svg.
        preserveAspectRatio="none"
      >
        <rect
          x="0.5"
          y="0.5"
          width="calc(100% - 1px)"
          height="calc(100% - 1px)"
          rx="15.5"
          ry="15.5"
          fill="none"
          // 30% white per design spec — explicit colour rather than
          // currentColor, so the cascade from the parent's `color`
          // (which drives the icon + label) doesn't pull the border
          // up to full white. The icon + label hover affordance
          // remains via the parent's _hover; the border stays static.
          stroke="rgba(255, 255, 255, 0.30)"
          strokeWidth="1"
          strokeDasharray="5 5"
        />
      </Box>
      {/*
        Plus icon inside an outline circle. We draw both the circle and
        the cross from a single SVG so currentColor drives both — the
        hover state colour-shift cascades through with no extra props.
      */}
      <Icon viewBox="0 0 32 32" w="32px" h="32px" aria-hidden="true">
        <circle
          cx="16"
          cy="16"
          r="15"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M16 10V22M10 16H22"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
      </Icon>
      <Text
        fontFamily="Bricolage Grotesque"
        fontWeight={700}
        fontSize="18px"
        lineHeight="1.25"
      >
        {label}
      </Text>
    </Flex>
  );
}
